/* eslint-disable no-param-reassign */
import { format } from 'date-fns';
import { timeStrToInt } from '@/helpers/dateTime';
import companies from '@/modules/Flexigo/services/companies';
import dashboardService from '../../services/dashboard';
import router from '../../router';

const getRouteType = () => {
  const { params } = router.currentRoute;
  return params.type || 'FLEXIRIDE_POOL';
};

const getStatusDot = (value, threshold) => {
  if (!threshold || value >= threshold) return null;

  const diff = threshold - value;
  const tenPercentOfThreshold = threshold * 0.1;
  const twentyPercentOfThreshold = threshold * 0.2;

  if (diff >= tenPercentOfThreshold && diff <= twentyPercentOfThreshold) return 'orange';
  if (diff >= twentyPercentOfThreshold) return 'red';

  return null;
};

// For metrics where higher values are better (e.g., OTP, acceptance rate)
// Shows red dot when value is below threshold
const getStatusDotForHigherIsBetter = (value = 0, threshold) => {
  if (!threshold || value >= threshold) return null;
  return 'red';
};

// For metrics where lower values are better (e.g., average deviation)
// Shows red dot when value is above or equal to threshold
const getStatusDotForLowerIsBetter = (value = 0, threshold) => {
  if (!threshold || value < threshold) return null;
  return 'red';
};

const state = {
  kpiData: {},
  demandItems: [],
  efficiencyItems: [],
  otpItems: [],
  usageItems: [],
  operationalHoursList: {},
  isLoading: false,
  error: null,
  settingsForKpi: {},
};

const getters = {
  kpiItems: (storeState, storeGetters) => {
    const {
      totalRequests,
      preBooked,
      onDemand,
      averageDeviation,
      acceptanceRate,
      totalRidership,
      ridersPerHour,
      pickupOtp,
      dropoffOtp,
      averageDeviationPickup,
      averageDeviationDropoff,
      riderNoShows,
      cancelledByRider,
      cancelledByAdmin,
      cancelledAtPlanning,
      pooledTrips,
      totalTripsCompleted,
    } = storeState.kpiData;

    const {
      avgDeviationWarningThresholdMin,
      pickupOtpWarningThresholdMin,
      acceptanceRateWarningThresholdMin,
      dropoffOtpWarningThresholdMin,
      riderPerHourWarningThresholdMin,
    } = storeGetters.settingsForKpi;

    const baseUrl = process.env.BASE_URL;

    const noShowCount = riderNoShows || 0;
    const totalAttempts = totalRidership + noShowCount;
    const noShowPercentage = totalAttempts > 0 ? ((noShowCount / totalAttempts) * 100).toFixed(2) : 0;

    const pooledCount = pooledTrips || 0;
    // Changed denominator from totalRidership to totalTripsCompleted
    // to show pooled percentage as: (pooled trips / completed trips) * 100
    // This provides a more accurate metric of pooling efficiency among successful trips
    const pooledPercentage = totalTripsCompleted > 0 ? ((pooledCount / totalTripsCompleted) * 100).toFixed(2) : 0;

    return [
      {
        id: 1,
        icon: `${baseUrl}icons/dashboard/icon-total-requests.svg`,
        title: totalRequests?.toString() || '0',
        description: 'flexigo.dashboard.totalRequests',
        statusDot: null,
      },
      {
        id: 2,
        icon: `${baseUrl}icons/dashboard/icon-total-requests.svg`,
        title: `${preBooked || 0} / ${onDemand || 0}`,
        description: 'flexigo.dashboard.preBookedOnDemand',
        statusDot: null,
      },
      {
        id: 3,
        icon: `${baseUrl}icons/dashboard/icon-average-deviation.svg`,
        title: averageDeviation ? `${averageDeviation} min` : '0 min',
        description: 'flexigo.dashboard.averageOfferDeviation',
        statusDot: getStatusDotForLowerIsBetter(averageDeviation, avgDeviationWarningThresholdMin),
      },
      {
        id: 4,
        icon: `${baseUrl}icons/dashboard/icon-acceptance-rate.svg`,
        title: acceptanceRate ? `${acceptanceRate}%` : 'N/A',
        description: 'flexigo.dashboard.acceptanceRate',
        statusDot: getStatusDot(acceptanceRate, acceptanceRateWarningThresholdMin),
      },
      {
        id: 5,
        icon: `${baseUrl}icons/dashboard/icon-total-ridership.svg`,
        title: totalRidership?.toString() || '0',
        description: 'flexigo.dashboard.totalRidership',
        statusDot: null,
      },
      {
        id: 6,
        icon: `${baseUrl}icons/dashboard/icon-riders-per-hour.svg`,
        title: ridersPerHour?.toString() || '0',
        description: 'flexigo.dashboard.ridersPerHour',
        statusDot: getStatusDotForHigherIsBetter(ridersPerHour, riderPerHourWarningThresholdMin),
      },
      {
        id: 7,
        icon: `${baseUrl}icons/dashboard/icon-pickup-otp.svg`,
        title: pickupOtp ? `${pickupOtp}%` : 'N/A',
        description: 'flexigo.dashboard.pickUpOtp',
        statusDot: getStatusDotForHigherIsBetter(pickupOtp, pickupOtpWarningThresholdMin),
      },
      {
        id: 8,
        icon: `${baseUrl}icons/dashboard/icon-dropoff-otp.svg`,
        title: dropoffOtp ? `${dropoffOtp}%` : 'N/A',
        description: 'flexigo.dashboard.dropOffOtp',
        statusDot: getStatusDot(dropoffOtp, dropoffOtpWarningThresholdMin),
      },
      {
        id: 9,
        icon: `${baseUrl}icons/dashboard/icon-avg-dropoff-deviation.svg`,
        title: `${averageDeviationPickup || 0} min / ${averageDeviationDropoff || 0} min`,
        description: 'flexigo.dashboard.averageDeviationPickupDropoff',
        statusDot: null,
      },
      {
        id: 10,
        icon: `${baseUrl}icons/dashboard/icon-riderno-show.svg`,
        title: `${noShowCount} - ${noShowPercentage}%`,
        description: 'flexigo.dashboard.riderNoShows',
        statusDot: null,
      },
      {
        id: 11,
        icon: `${baseUrl}icons/dashboard/icon-cancelled.svg`,
        title: `${cancelledByRider || 0} / ${cancelledByAdmin || 0} / ${cancelledAtPlanning || 0}`,
        description: 'flexigo.dashboard.cancelledByRiderAdminPlanning',
        statusDot: null,
      },
      {
        id: 12,
        icon: `${baseUrl}icons/dashboard/icon-pooled-trips.svg`,
        title: `${pooledCount} - ${pooledPercentage}%`,
        description: 'flexigo.dashboard.pooledTrips',
        statusDot: null,
      },
    ];
  },
  isLoading: (storeState) => storeState.isLoading,
  error: (storeState) => storeState.error,
  demandItems: (storeState) => storeState.demandItems,
  otpItems: (storeState) => storeState.otpItems,
  efficiencyItems: (storeState) => storeState.efficiencyItems,
  usageItems: (storeState) => storeState.usageItems,
  operationalHoursList: (storeState) => storeState.operationalHoursList,
  settingsForKpi: (storeState) => storeState.settingsForKpi,
};

const mutations = {
  SET_KPI_DATA(storeState, data) {
    storeState.kpiData = data;
  },
  SET_LOADING(storeState, isLoading) {
    storeState.isLoading = isLoading;
  },
  SET_ERROR(storeState, error) {
    storeState.error = error;
  },
  SET_DEMAND_ITEMS(storeState, demandItems) {
    storeState.demandItems = demandItems;
  },
  SET_EFFICIENCY_ITEMS(storeState, efficiencyItems) {
    storeState.efficiencyItems = efficiencyItems;
  },
  SET_OTP_ITEMS(storeState, otpItems) {
    storeState.otpItems = otpItems;
  },
  SET_USAGE_ITEMS(storeState, usageItems) {
    storeState.usageItems = usageItems;
  },
  SET_OPERATIONAL_HOURS_LIST(storeState, operationalHoursList) {
    storeState.operationalHoursList = operationalHoursList;
  },
  SET_SETTINGS_FOR_KPI(storeState, settings) {
    storeState.settingsForKpi = settings;
  },
};

const actions = {
  async fetchKpiData({ commit, rootState }, { startDate, endDate }) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      // Ensure dates are Date objects
      const startDateObj = startDate instanceof Date ? startDate : new Date(startDate);
      const endDateObj = endDate instanceof Date ? endDate : new Date(endDate);

      const formattedFromDate = format(startDateObj, 'yyyyMMdd');
      const formattedToDate = format(endDateObj, 'yyyyMMdd');
      const type = getRouteType();
      const companyId = rootState.companySettings?.companyId;

      const params = {
        startDay: formattedFromDate,
        endDay: formattedToDate,
        type,
      };

      const {
        operationalHours,
        onDemandPickUpOtpBuffer,
        onDemandDropOffOtpBuffer,
        avgDeviationWarningThresholdMin,
        acceptanceRateWarningThresholdMin,
        riderPerHourWarningThresholdMin,
        pickupOtpWarningThresholdMin,
        dropoffOtpWarningThresholdMin,
        avgPickupDeviationOtpWarningThresholdMin,
        avgDropoffDeviationOtpWarningThresholdMin,
      } = (await companies.getFlexiRidePoolSettings(type, companyId)) || {};

      commit('SET_OPERATIONAL_HOURS_LIST', operationalHours);
      commit('SET_SETTINGS_FOR_KPI', {
        onDemandPickUpOtpBuffer,
        onDemandDropOffOtpBuffer,
        avgDeviationWarningThresholdMin,
        acceptanceRateWarningThresholdMin,
        riderPerHourWarningThresholdMin,
        pickupOtpWarningThresholdMin,
        dropoffOtpWarningThresholdMin,
        avgPickupDeviationOtpWarningThresholdMin,
        avgDropoffDeviationOtpWarningThresholdMin,
      });

      const response = await dashboardService.getOnDemandKpiItems(params);
      commit('SET_KPI_DATA', response);
    } catch (error) {
      console.error('Error fetching On-Demand KPI data:', error);
      commit('SET_ERROR', error.message || 'Failed to fetch KPI data');
    } finally {
      commit('SET_LOADING', false);
    }
  },
  async fetchDemandData({ commit }, { startDate, endDate, fromHour, toHour }) {
    try {
      // Ensure dates are Date objects
      const startDateObj = startDate instanceof Date ? startDate : new Date(startDate);
      const endDateObj = endDate instanceof Date ? endDate : new Date(endDate);

      const formattedFromDate = format(startDateObj, 'yyyyMMdd');
      const formattedToDate = format(endDateObj, 'yyyyMMdd');
      const type = getRouteType();

      const params = {
        startDay: formattedFromDate,
        endDay: formattedToDate,
        fromHour: timeStrToInt(fromHour),
        toHour: timeStrToInt(toHour),
        type,
      };
      const response = await dashboardService.getOnDemandData(params);
      commit('SET_DEMAND_ITEMS', response);
    } catch (error) {
      console.error('Error fetching On-Demand demand data:', error);
    }
  },
  async fetchEfficiencyData({ commit }, { startDate, endDate, fromHour, toHour }) {
    try {
      // Ensure dates are Date objects
      const startDateObj = startDate instanceof Date ? startDate : new Date(startDate);
      const endDateObj = endDate instanceof Date ? endDate : new Date(endDate);

      const formattedFromDate = format(startDateObj, 'yyyyMMdd');
      const formattedToDate = format(endDateObj, 'yyyyMMdd');
      const type = getRouteType();

      const params = {
        startDay: formattedFromDate,
        endDay: formattedToDate,
        fromHour: timeStrToInt(fromHour),
        toHour: timeStrToInt(toHour),
        type,
      };
      const response = await dashboardService.getEfficiencyData(params);
      commit('SET_EFFICIENCY_ITEMS', response);
    } catch (error) {
      console.error('Error fetching On-Demand demand data:', error);
    }
  },
  async fetchOtpData(
    { commit },
    { startDate, endDate, fromHour, toHour, vehicleIds = [], driverIds = [], threshold = 0 },
  ) {
    try {
      // Ensure dates are Date objects
      const startDateObj = startDate instanceof Date ? startDate : new Date(startDate);
      const endDateObj = endDate instanceof Date ? endDate : new Date(endDate);

      const formattedFromDate = format(startDateObj, 'yyyyMMdd');
      const formattedToDate = format(endDateObj, 'yyyyMMdd');
      const type = getRouteType();

      const params = {
        startDay: formattedFromDate,
        endDay: formattedToDate,
        fromHour: timeStrToInt(fromHour),
        toHour: timeStrToInt(toHour),
        vehicleIds,
        driverIds,
        threshold,
        type,
      };
      const response = await dashboardService.getOtpData(params);
      commit('SET_OTP_ITEMS', response);
    } catch (error) {
      console.error('Error fetching On-Demand demand data:', error);
    }
  },
  async fetchUsageData({ commit }, { startDate, endDate, fromHour, toHour }) {
    try {
      // Ensure dates are Date objects
      const startDateObj = startDate instanceof Date ? startDate : new Date(startDate);
      const endDateObj = endDate instanceof Date ? endDate : new Date(endDate);

      const formattedFromDate = format(startDateObj, 'yyyyMMdd');
      const formattedToDate = format(endDateObj, 'yyyyMMdd');
      const type = getRouteType();

      const params = {
        startDay: formattedFromDate,
        endDay: formattedToDate,
        fromHour: timeStrToInt(fromHour),
        toHour: timeStrToInt(toHour),
        type,
      };
      const response = await dashboardService.getUsageData(params);
      commit('SET_USAGE_ITEMS', response);
    } catch (error) {
      console.error('Error fetching On-Demand usage data:', error);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
